<!--@format-->
<template>
    <div
        class="app-input"
        :class="{
            error: !isValid,
            'has-icon': $slots.inputIcon || (isPassword && showToggleIcon),
            'has-prefix': prefix,
        }"
    >
        <label v-if="label">{{ label }} <span class="required" v-if="required">*</span></label>
        <div class="input-container">
            <div v-if="prefix" class="prefix">{{ prefix }}</div>
            <input
                ref="input"
                :type="isPassword ? (passwordVisible ? 'text' : 'password') : type"
                :value="value"
                :placeholder="placeholder ? placeholder : label"
                :min="min"
                :max="max"
                :maxlength="maxLength"
                :step="step"
                :disabled="disabled"
                :autocomplete="autoComplete"
                :required="required"
                @input="updateValue()"
                @focus="focus()"
                @blur="blur()"
                @change="change()"
                @keyup="keyup"
                @keydown.enter="keydown"
            />
            <div v-if="suffix" class="suffix">{{ suffix }}</div>
            <div
                v-if="isPassword && showToggleIcon"
                class="icon-container show-password"
                @click="togglePasswordVisibility"
            >
                <show-input-password-svg v-if="!passwordVisible" />
                <hide-input-password-svg v-else />
            </div>
            <div class="icon-container">
                <slot name="inputIcon"></slot>
            </div>
        </div>
        <span v-if="isValid && description" class="description">{{ description }}</span>
        <span v-else-if="!isValid && error" class="error-message">{{ error }}</span>
    </div>
</template>

<script>
const ShowInputPasswordSvg = () => import('@/components/svg/show-input-password-svg.vue');
const HideInputPasswordSvg = () => import('@/components/svg/hide-input-password-svg.vue');

export default {
    components: {
        'show-input-password-svg': ShowInputPasswordSvg,
        'hide-input-password-svg': HideInputPasswordSvg,
    },

    props: {
        showToggleIcon: {
            type: Boolean,
            default: false,
        },

        type: {
            type: String,
            default: 'text',
        },

        label: {
            type: String,
        },

        placeholder: {
            type: String,
        },

        value: {
            type: [String, Number],
        },

        min: {
            type: [String, Number],
        },

        max: {
            type: [String, Number],
        },

        step: {
            type: String,
            default: '1',
        },

        maxLength: {
            type: Number,
        },

        description: {
            type: String,
        },

        prefix: {
            type: String,
        },

        suffix: {
            type: String,
            default: null,
        },

        isValid: {
            type: Boolean,
            default: true,
        },

        error: {
            type: String,
        },

        disabled: {
            type: Boolean,
        },

        autoComplete: {
            type: String,
            default: 'on',
        },

        required: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            passwordVisible: false,
        };
    },

    computed: {
        isPassword() {
            return this.type === 'password';
        },
    },

    methods: {
        updateValue() {
            this.$emit('input', this.$refs.input.value);
        },

        focus() {
            this.$emit('focus');
        },

        keyup(e) {
            this.$emit('keyup', e);
        },

        blur() {
            this.$emit('blur');
        },

        change() {
            this.$emit('change');
        },

        keydown(event) {
            this.$emit('keydown:enter', event.target.value);
        },

        togglePasswordVisibility() {
            this.passwordVisible = !this.passwordVisible;
        },
    },
};
</script>

<style>
.app-input .icon-container {
    display: none;
    right: 8px;
    top: 12px;
    width: 20px;
    height: 20px;
}

.app-input.has-icon .icon-container {
    display: block;
}

.app-input .icon-container svg {
    width: 20px;
    height: 20px;
    width: 100%;
}

.app-input .icon-container path {
    fill: var(--color-fill-accent-silvergray);
    transition: fill 0.25s ease-in;
}

.app-input {
    position: relative;
}

.app-input .input-container {
    position: relative;
}

.app-input input::placeholder {
    color: var(--color-text-subtlest);
}

.app-input input {
    background: var(--color-fill-field-active);
    font: normal 16px/1em 'Roboto';
    border: 1px solid var(--color-border-field-active);
    padding: 8px 16px;
    color: var(--color-text-default);
    outline: none;
    width: 100%;
    border-radius: 20px;
}

.app-input input:hover {
    border-color: var(--color-border-field-hover);
    background-color: var(--color-fill-field-hover);
    border-width: 2px;
    padding: 7px 15px;
}

.app-input input:active,
.app-input input:focus {
    border: solid var(--color-border-field-input);
    background-color: var(--color-fill-field-input);
    border-width: 2px;
    padding: 7px 15px;
}

.app-input label {
    display: block;
    font: 500 16px/1em 'Roboto';
    margin-bottom: 7px;
    color: var(--color-text-default);
}

.app-input.has-prefix .input-container input {
    padding-left: 30px;
}

.app-input .prefix {
    position: absolute;
    top: 8px;
    left: 15px;
}

.app-input .suffix {
    position: absolute;
    top: 10px;
    right: 16px;
}

.app-input .description,
.app-input .error-message {
    display: block;
    margin: 0.8em 0 1.25em 0;
    font-size: 12px;
    color: var(--color-text-subtle);
}

.app-input.error label,
.app-input.error .error-message {
    color: var(--color-text-warning);
}

.app-input.error input {
    border-color: var(--color-border-field-error);
}

.app-input.has-icon .input-container .icon-container {
    position: absolute;
    top: 5px;
}

.app-input.has-icon input {
    padding-right: 35px;
}

.app-input .icon-container .k5-email-outline-svg path,
.app-input .icon-container .k5-email-outline-svg rect {
    fill: none;
    stroke: var(--color-fill-accent-silvergray);
}

.app-input .icon-container .g5-call-outline-svg path {
    fill: none;
    stroke: var(--color-fill-accent-silvergray);
}

.app-input .show-password {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
}

.app-input .show-password svg {
    width: 20px;
    height: 20px;
}

.app-input .show-password path {
    fill: none;
}

.app-input.has-icon .show-password {
    padding-right: 35px;
}

.app-input .required {
    color: var(--color-text-warning);
}
</style>
